import React, { useState } from 'react'
import styled from 'styled-components'
import * as UMEColors from '@bit/ume.design-kit.ui/utils/_colors'
import { RiskBreadcrumbs } from '../../molecules/LimitsModificationPage/RiskBreadcrumbs'
import { Heading, Box } from 'grommet'
import { Button, Tooltip, Modal } from 'antd'
import XLSX from 'xlsx'
import { toaster } from '../../App'
import { LoadingIcon } from '../../atoms/Loading-icon/LoadingIcon'
import { FinancingOptionsWarnings } from '../../organisms/FinancingOptionsModifications/FinancingOptionsWarnings'
import { get_header_row } from '../../config/utils'
import { FINANCING_OPTIONS_TEMPLATE } from './FinancingOptionsConfig'
import { bffBackofficeApiService } from '../../services/bff-backoffice'
import { ICreateFinancingOptionsValidationResult, IFinancingOption } from '../../services/bff-backoffice/coordinator/interfaces/financing-options.interfaces'

interface IFinancingOptionsModificationsPageProps {
  history: any
  location: any
}

interface IFinancingOptionsModificationsPageState {
  isLoadingWarnings: boolean
  validationResult?: ICreateFinancingOptionsValidationResult,
  isPuttingNewFinancingOptions: boolean
  newFinancingOptionsJson?: IFinancingOption[]
}

const initialState: IFinancingOptionsModificationsPageState = {
  isLoadingWarnings: false,
  isPuttingNewFinancingOptions: false,
}

export const FinancingOptionsModificationsPage = (props: IFinancingOptionsModificationsPageProps) => {
  const [state, setState] = useState<IFinancingOptionsModificationsPageState>(initialState)

  // Gets the inputted file from the user
  // - Validates it's format (header rows)
  // - Creates a json array
  // - Sends to server
  const handleSelectedFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, validationResult: undefined, isLoadingWarnings: true })

    const files = e.target.files

    if (!files || files.length === 0) {
      setState({ ...state, isLoadingWarnings: false })
      return toaster.showErrorToast(`Selecione um arquivo`)
    }
    const file = files[0]

    const bufferData = await file.arrayBuffer()

    // Read the .xlsx file
    const data = XLSX.read(bufferData, { type: 'buffer' })

    // Get all sheet's names
    const sheetNames = data.SheetNames

    // No sheet or multiple sheets error
    if (sheetNames.length !== 1) {
      setState({ ...state, isLoadingWarnings: false })
      return toaster.showErrorToast(`Planilha deve ter uma Sheet!`)
    }

    // Get the worksheet
    const worksheet = data.Sheets[sheetNames[0]]

    // Get the header row
    const headerRow = get_header_row(worksheet)

    // Check if the header row is as expected
    const expectedHeadersArray = ['borrowerId', 'retailerId', 'storeId', 'parcelamento', 'tx_de_juros']
    for (const expectedHeader of expectedHeadersArray) {
      if (!headerRow.find(element => element === expectedHeader)) {
        setState({ ...state, isLoadingWarnings: false })
        return toaster.showErrorToast(`Coluna ${expectedHeader} faltando na tabela.`)
      }
    }

    let jsonFromTable: any[] = XLSX.utils.sheet_to_json(data.Sheets[sheetNames[0]])
    const jsonBody: any[] = []

    // Validates the financingOptions json array
    for (let element of jsonFromTable) {
      const borrowerId = element.borrowerId ? String(element.borrowerId) : undefined
      const retailerId = element.retailerId ? String(element.retailerId) : undefined
      const storeId = element.storeId ? String(element.storeId) : undefined
      const numberOfInstallments = Number(element.parcelamento)

      // If it's not a positive number, return an error
      if (!numberOfInstallments) {
        setState({ ...state, isLoadingWarnings: false })
        return toaster.showErrorToast('Parcelamento deve ser um número positivo!')
      }

      // Treat string, it can be provided as: 2.99 or 2.99%
      let interestRate
      if (isNaN(element.tx_de_juros)) {
        interestRate = Number(String(element.tx_de_juros).replace('%', ''))
        if (isNaN(interestRate)) {
          setState({ ...state, isLoadingWarnings: false })
          return toaster.showErrorToast('Porcentagem no formato incorreto!')
        }
      }
      // Trasnform into expected value
      //  Instead of sending: 4.99, send 0.0499
      interestRate = Number(interestRate) / 100

      jsonBody.push({
        borrowerId,
        retailerId: retailerId,
        storeId: storeId,
        numberOfInstallments,
        interestRate,
      })
    }

    bffBackofficeApiService.coordinator
      .validateNewFinancingOptions({ financingOptions: jsonBody })
      .then(result => {
        toaster.showSuccessToast(`Planilha carregada com sucesso.`)
        setState({ ...state, validationResult: result, isLoadingWarnings: false, newFinancingOptionsJson: jsonBody })
      })
      .catch(error => {
        if (error && error.response && error.response.data) {
          Modal.error({
            title: 'Erro ao submeter planilha',
            content: JSON.stringify(error.response.data),
          })
        } else {
          toaster.showErrorToast(`Erro no servidor`)
        }
        setState({ ...state, isLoadingWarnings: false })
      })
  }

  // Changes the event value to default, so the onChange event is triggered even if
  //   the same file is uploaded.
  const handleClickInputFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = ""
  }

  // Creates a template table and sends to user
  const handleDownloadTableTemplate = () => {
    const sheet = XLSX.utils.json_to_sheet(FINANCING_OPTIONS_TEMPLATE)
    XLSX.utils.sheet_add_json(sheet, [], { origin: 'A3' })

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, sheet)
    XLSX.writeFile(wb, 'financing_options_template.xlsx')
  }

  // Clear screen
  const clearScreen = () => {
    setState({ ...state, validationResult: undefined, isLoadingWarnings: false, isPuttingNewFinancingOptions: false })
  }

  // Updates limits on the server!
  const handleConfirmButton = () => {
    setState({ ...state, isPuttingNewFinancingOptions: true })

    if (!state.newFinancingOptionsJson) {
      return
    }

    bffBackofficeApiService.coordinator
      .createFinancingOptions({ financingOptions: state.newFinancingOptionsJson })
      .then(() => {
        Modal.success({
          title: 'Opções de financiamento alteradas com sucesso!',
          maskClosable: true,
          centered: true,
        })
        clearScreen()
      })
      .catch(error => {
        if (error && error.response && error.response.data) {
          Modal.error({
            title: 'Erro ao submeter planilha',
            content: JSON.stringify(error.response.data),
          })
        } else {
          toaster.showErrorToast(`Erro no servidor`)
        }
      })
  }

  const { isLoadingWarnings, validationResult, isPuttingNewFinancingOptions } = state
  const hasErrors = validationResult && validationResult.errors.length > 0 ? true : false

  return (
    <Container>
      <div style={{ marginTop: '3vh' }}>
        <RiskBreadcrumbs screenName='Modificação de Opções de Financiamento' />
      </div>

      <Title>
        <Heading level={2}> Modificação de Opções de Financiamento </Heading>
        <DownloadTableTemplateButton type="primary" size="middle" onClick={handleDownloadTableTemplate}>
          Download template da planilha
        </DownloadTableTemplateButton>
      </Title>

      <Box direction="row" margin={{ top: '5vh' }}>
        <UploadText>
          Selecione uma planilha no formato <strong>.xlsx</strong> para validação
        </UploadText>

        <FileUpload type="file" accept=".xlsx" onChange={handleSelectedFile} onClick={handleClickInputFile} />
      </Box>

      <Box direction="row" margin={{ top: '5vh' }}>
        {isLoadingWarnings ? (
          <Box flex margin={{ top: '8vh' }} align="center" justify="center">
            <LoadingIcon />
          </Box>
        ) : (
          <FinancingOptionsWarnings validationResult={validationResult} />
        )}
      </Box>

      {validationResult ? (
        <Box direction="row" margin={{ top: '3vh' }} align="center" justify="end" gap="medium">
          <Button onClick={clearScreen}> Cancelar </Button>
          <Tooltip title={hasErrors ? 'Corrija os erros notificados para efetivar as alterações' : ''}>
            <ConfirmButton
              onClick={handleConfirmButton}
              loading={isPuttingNewFinancingOptions}
              type="primary"
              disabled={hasErrors}
            >
              Efetivar alterações
            </ConfirmButton>
          </Tooltip>
        </Box>
      ) : (
        <></>
      )}
    </Container>
  )
}

const UploadText = styled.span`
  color: #000000;
  font-family: Roboto;
  font-size: 16px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 19px;
`

const FileUpload = styled.input`
  outline: none;
  margin-left: 3vw;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${UMEColors.gray.azure};
  min-height: 94vh;
  padding: 16px 64px;
  width: 100%;

  text-align: initial;
`

const Title = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;

  margin-top: 3vh;
`
const DownloadTableTemplateButton = styled(Button)`
  display: inline-block;

  border: none;

  // TEXT
  color: ${UMEColors.white.primary};
  font-family: Roboto;
  font-weight: bold;
  letter-spacing: 0;
  text-align: center;

  // BUTTON
  border-radius: 2px;
  background-color: ${UMEColors.gray.primary};
  box-shadow: 0 2px 15px 0 rgba(0, 0, 0, 0.1);

  &:hover {
    cursor: pointer;
    transition: 0.3s;
    background-color: ${UMEColors.gray.primary};
    opacity: 0.5;
  }

  &:focus {
    background-color: ${UMEColors.gray.primary};
  }

  &:active {
    background-color: ${UMEColors.gray.primary};
  }
`

const ConfirmButton = styled(Button)`
  display: inline-block;

  border: none;

  // TEXT
  color: ${UMEColors.white.primary};
  font-family: Roboto;
  font-weight: bold;
  letter-spacing: 0;
  text-align: center;

  // BUTTON
  border-radius: 2px;
  background-color: ${UMEColors.green.dark};
  box-shadow: 0 2px 15px 0 rgba(0, 0, 0, 0.1);

  &:hover {
    cursor: pointer;
    transition: 0.3s;
    background-color: ${UMEColors.green.dark};
    opacity: 0.5;
  }

  &:focus {
    background-color: ${UMEColors.green.dark};
  }

  &:active {
    background-color: ${UMEColors.green.dark};
  }
`
